import React from "react";

import Panel from "ui/Panel";

import { formatBytes, formatNumber, formatMs } from "utils/format";

import { Data } from "ui/Graph/util";
import { useDateRange } from "providers/WithDateRange";

import { LineSeries, AreaSeries, ThresholdSeries } from "ui/Graph/Series";

import GraphSection from "ui/Graph/GraphSection";
import DateRangeGraph from "ui/Graph/DateRangeGraph";
import { useStorageAndIoQuery } from "./gql/Query.generated";

type Props = {
  amazonRdsEnhanced: boolean;
  serverId: string;
  isAuroraPostgres: boolean;
  storageMountpoint: string;
  systemType: string;
  isAzureCosmos: boolean;
};

const StorageAndIO: React.FunctionComponent<Props> = ({
  amazonRdsEnhanced,
  serverId,
  isAuroraPostgres,
  storageMountpoint,
  systemType,
  isAzureCosmos,
}) => {
  const { from, to } = useDateRange();

  const { data, loading, error } = useStorageAndIoQuery({
    variables: {
      serverId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });

  const noData = !loading && !error && data.getSystemStats == null;

  const storageSpaceChart = (
    <Panel
      title={
        (systemType == "physical" &&
          storageMountpoint &&
          `Storage Space for ${storageMountpoint}`) ||
        "Storage Space"
      }
    >
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: formatBytes,
            },
          }}
          series={[
            {
              type: AreaSeries,
              key: "storageSpaceUsed",
              label: "Used Storage",
              tipLabel: "Used",
              color: "#e3b514",
            },
            {
              type: AreaSeries,
              key: "storageSpaceFree",
              label: "Free Storage",
              tipLabel: "Free",
              color: "#c3d52a",
            },
            {
              type: LineSeries,
              key: "storageSpaceTotal",
              label: "Total Storage",
              tipLabel: "Total",
              color: "red",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const collectorVersion =
    data?.getServerDetails?.lastSnapshot?.collectorVersion;
  const hasAuroraStorageFix = collectorVersion >= 7002;

  const storageUsageSecondaryTitle = isAuroraPostgres && (
    <span>
      <strong>Note:</strong>{" "}
      {hasAuroraStorageFix
        ? "Cluster-wide storage usage, may be up to 3 hours delayed"
        : "Storage usage may be inaccurate. Update the collector for accurate data"}
    </span>
  );

  const storageUsageChart = (
    <Panel title="Storage Usage" secondaryTitle={storageUsageSecondaryTitle}>
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: formatBytes,
            },
          }}
          series={[
            {
              type: AreaSeries,
              key: "storageSpaceUsed",
              label: "Used Storage",
              tipLabel: "Used",
              color: "orange",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const ioUtilizationChart = (
    <Panel title="I/O Utilization">
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: "pct from ratio",
            },
          }}
          series={[
            {
              type: AreaSeries,
              key: "storageUtilization",
              label: "% Utilization",
              color: "#e3b514",
            },
            {
              type: ThresholdSeries,
              key: "storageUtilizationPct90",
              label: "90% Limit",
              tipLabel: "90%",
              color: "grey",
            },
            {
              type: ThresholdSeries,
              key: "storageUtilizationPct100",
              label: "100% Limit (Overloaded)",
              tipLabel: "100%",
              color: "red",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const iopsChart = (
    <Panel title="IOPS">
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              tipFormat: (y: number): string => formatNumber(y) + " / sec",
            },
          }}
          series={[
            {
              type: LineSeries,
              key: "storageIopsRead",
              label: "Read IOPS",
            },
            {
              type: LineSeries,
              key: "storageIopsWrite",
              label: "Write IOPS",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const ioLatencyChart = (
    <Panel title="I/O Latency">
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: (y: number): string => formatMs(y),
            },
          }}
          series={[
            {
              type: LineSeries,
              key: "storageLatencyRead",
              label: "Average Read Wait",
              tipLabel: "Read Wait",
            },
            {
              type: LineSeries,
              key: "storageLatencyWrite",
              label: "Average Write Wait",
              tipLabel: "Write Wait",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const ioThroughputChart = (
    <Panel title="I/O Throughput">
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: "bytes/sec",
            },
          }}
          series={[
            {
              key: "storageThroughputRead",
              label: "Read Throughput",
              tipLabel: "Read",
            },
            {
              key: "storageThroughputWrite",
              label: "Write Throughput",
              tipLabel: "Write",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  const ioQueueDepthChart = (
    <Panel title="I/O Queue Depth">
      <GraphSection noData={noData} loading={loading} error={error}>
        <DateRangeGraph
          data={data?.getSystemStats as unknown as Data}
          axes={{
            left: {
              format: formatNumber,
            },
          }}
          series={[
            {
              key: "storageQueueDepth",
              label: "IOPS in Progress",
              tipLabel: "IOPS",
            },
          ]}
        />
      </GraphSection>
    </Panel>
  );

  // Heroku Postgres has very limited I/O and Storage metrics
  if (systemType == "heroku") {
    return iopsChart;
  }

  // Tembo currently has limited Storage metrics
  if (systemType == "tembo") {
    return storageSpaceChart;
  }

  // Azure
  if (systemType == "azure_database") {
    if (isAzureCosmos) {
      return storageUsageChart;
    }
    return (
      <>
        {storageUsageChart}
        {iopsChart}
        {ioThroughputChart}
        {ioQueueDepthChart}
      </>
    );
  }

  // Crunchy Bridge
  if (systemType == "crunchy_bridge") {
    return (
      <>
        {storageSpaceChart}
        {iopsChart}
      </>
    );
  }

  return (
    <>
      {isAuroraPostgres ? storageUsageChart : storageSpaceChart}
      {(systemType != "amazon_rds" ||
        (amazonRdsEnhanced && !isAuroraPostgres)) &&
        ioUtilizationChart}
      {iopsChart}
      {ioLatencyChart}
      {ioThroughputChart}
      {ioQueueDepthChart}
    </>
  );
};

export default StorageAndIO;
